import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { addDays, formatGhs } from '../format';
import { CATEGORIES, CATEGORY_LABEL, type StockCategory } from '../api/stock';
import { useInsights, type ActivityInsights, type InventoryInsights, type RestaurantInsights, type RoomsInsights } from '../api/insights';
import { Badge, ErrorNote, Field, Loading } from '../ui/kit';
import { ChartCard, Columns, Empty, HBars, LineChart, Split, StatTile, fmtCedisShort, fmtInt, fmtPct, type ColumnPoint } from '../ui/charts';
import { PageHead } from './shared';

type Tab = 'rooms' | 'restaurant' | 'inventory' | 'activity';

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const hh = (h: number): string => String(h).padStart(2, '0');
const shortDate = (iso: string): string => new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
const monthLabel = (m: string): string => new Date(`${m}-01T00:00:00`).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
const argmax = (xs: number[]): number => (Math.max(0, ...xs) === 0 ? -1 : xs.indexOf(Math.max(...xs)));
const plural = (n: number, one: string, many = `${one}s`): string => `${fmtInt(n)} ${n === 1 ? one : many}`;
const labelled = (map: Record<string, string>, key: string): string => map[key] ?? key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

const SOURCE: Record<string, string> = { FRONT_DESK: 'Front desk', PHONE: 'Phone', WHATSAPP: 'WhatsApp', WEBSITE: 'Website', WALK_IN: 'Walk-in' };
const TENDER: Record<string, string> = { CASH: 'Cash', MOMO: 'Mobile money', CARD: 'Card', ROOM: 'Charged to room' };
const DEPT: Record<string, string> = { FOOD: 'Food', BEVERAGE: 'Drinks' };
const GROUP: Record<string, string> = { booking: 'Bookings', folio: 'Room charges', payment: 'Payments', staff: 'Staff logins', ledger: 'Accounts', stock: 'Stock', guest: 'Guests', taxrule: 'Taxes', property: 'Hotel details', order: 'Restaurant orders', bill: 'Restaurant bills', refund: 'Refunds', payroll: 'Payroll', laundry: 'Laundry', channel: 'Channels', message: 'Guest messages', roster: 'Roster', attendance: 'Attendance', housekeeping: 'Housekeeping' };
const ROLE: Record<string, string> = { OWNER: 'owner', MANAGER: 'manager', FRONT_DESK: 'front desk', ACCOUNTANT: 'accountant', HOUSEKEEPING: 'housekeeping', RESTAURANT: 'restaurant', KITCHEN: 'kitchen', DRIVER: 'driver', ATTENDANT: 'attendant' };
const prettyAction = (a: string): string => a.replace(/[._]/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

/** Charts that take the same data, kept to the shapes the chart kit draws. */
const hourColumns = (counts: number[]): ColumnPoint[] => counts.map((value, h) => ({ label: hh(h), value, title: `${hh(h)}:00 to ${hh(h)}:59` }));
const weekdayColumns = (counts: number[]): ColumnPoint[] => counts.map((value, i) => ({ label: WEEKDAYS[i]!.slice(0, 3), value, title: WEEKDAYS[i]! }));

export function Insights() {
  const { me, can } = useAuth();
  const today = me?.property.today ?? new Date().toISOString().slice(0, 10);
  const [tab, setTab] = useState<Tab>('rooms');
  const [from, setFrom] = useState(() => addDays(today, -29));
  const [to, setTo] = useState(today);
  const [preset, setPreset] = useState<string>('30');

  const presets = useMemo(
    () => [
      { key: '7', label: 'Last 7 days', from: addDays(today, -6) },
      { key: '30', label: 'Last 30 days', from: addDays(today, -29) },
      { key: '90', label: 'Last 90 days', from: addDays(today, -89) },
      { key: 'year', label: 'This year', from: `${today.slice(0, 4)}-01-01` },
    ],
    [today],
  );
  const rangeOk = from <= to && (Date.parse(to) - Date.parse(from)) / 86_400_000 < 366;
  const canActivity = can('audit.view');

  return (
    <div className="page viz">
      <PageHead eyebrow="Management" title="Insights" />

      {/* one row of filters, above everything it scopes */}
      <div className="viz-filters">
        <div className="seg" role="group" aria-label="Period">
          {presets.map((p) => (
            <button
              key={p.key}
              type="button"
              aria-pressed={preset === p.key}
              onClick={() => {
                setPreset(p.key);
                setFrom(p.from);
                setTo(today);
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
        <Field id="in-from" label="From">
          <input
            id="in-from"
            className="input"
            type="date"
            value={from}
            max={to}
            onChange={(e) => {
              setFrom(e.target.value);
              setPreset('custom');
            }}
          />
        </Field>
        <Field id="in-to" label="To">
          <input
            id="in-to"
            className="input"
            type="date"
            value={to}
            min={from}
            max={today}
            onChange={(e) => {
              setTo(e.target.value);
              setPreset('custom');
            }}
          />
        </Field>
      </div>
      {!rangeOk ? <div className="banner bad">Choose a start date that is not after the end date, and a period of at most a year.</div> : null}

      <div className="seg" role="group" aria-label="What to look at">
        <button type="button" aria-pressed={tab === 'rooms'} onClick={() => setTab('rooms')}>
          Rooms and bookings
        </button>
        <button type="button" aria-pressed={tab === 'restaurant'} onClick={() => setTab('restaurant')}>
          Restaurant and bar
        </button>
        <button type="button" aria-pressed={tab === 'inventory'} onClick={() => setTab('inventory')}>
          Inventory
        </button>
        {canActivity ? (
          <button type="button" aria-pressed={tab === 'activity'} onClick={() => setTab('activity')}>
            Activity
          </button>
        ) : null}
      </div>

      {rangeOk ? (
        <>
          {tab === 'rooms' ? <RoomsTab from={from} to={to} /> : null}
          {tab === 'restaurant' ? <RestaurantTab from={from} to={to} /> : null}
          {tab === 'inventory' ? <InventoryTab from={from} to={to} /> : null}
          {tab === 'activity' && canActivity ? <ActivityTab from={from} to={to} /> : null}
        </>
      ) : null}
    </div>
  );
}

/** Loading, error and the "previous numbers stay while new ones load" frame, shared by the four tabs. */
function Frame<T>({ q, children }: { q: { data: T | undefined; error: unknown; isPlaceholderData: boolean; refetch: () => unknown }; children: (d: T) => ReactNode }) {
  if (!q.data) return q.error ? <ErrorNote error={q.error} retry={() => void q.refetch()} /> : <Loading />;
  return <div className={`viz-body${q.isPlaceholderData ? ' stale' : ''}`}>{children(q.data)}</div>;
}

/* ------------------------------------------------------------------------------ rooms */
function RoomsTab({ from, to }: { from: string; to: string }) {
  const q = useInsights<RoomsInsights>('rooms', from, to);
  return (
    <Frame q={q}>
      {(d) => {
        const hour = argmax(d.madeByHour);
        const day = argmax(d.madeByWeekday);
        const arrive = argmax(d.checkInsByWeekday);
        const lostPct = d.totals.bookingsMade ? ((d.totals.cancelled + d.totals.noShows) / d.totals.bookingsMade) * 100 : 0;
        const rooms = d.byRoom.filter((r) => r.stays > 0);
        const unbooked = d.byRoom.filter((r) => r.stays === 0);
        return (
          <>
            <div className="viz-tiles">
              <StatTile label="Occupancy" value={fmtPct(d.totals.occupancyPct)} hint={`${d.roomCount} rooms, ${plural(d.range.days, 'night')}`} />
              <StatTile label="Stays started" value={fmtInt(d.totals.stays)} hint={`${plural(d.totals.nights, 'night')} sold${d.totals.averageNights ? `, ${d.totals.averageNights} each on average` : ''}`} />
              <StatTile label="Average rate" value={d.totals.averageRate ? formatGhs(d.totals.averageRate) : '—'} hint="per night, tax included" />
              <StatTile label="Bookings made" value={fmtInt(d.totals.bookingsMade)} hint={`${fmtPct(lostPct)} cancelled or no-show`} />
              <StatTile label="Room revenue booked" value={formatGhs(d.totals.revenue)} hint="stays starting in the period" />
            </div>

            {d.totals.bookingsMade > 0 && hour >= 0 && day >= 0 ? (
              <p className="viz-callout">
                Most bookings are made around <strong>{hh(hour)}:00</strong>, and most often on <strong>{WEEKDAYS[day]}s</strong>.{arrive >= 0 ? ` Most guests arrive on ${WEEKDAYS[arrive]}s.` : ''}
              </p>
            ) : null}

            <div className="viz-cards">
              <ChartCard
                title="Most booked rooms"
                note={d.unassigned ? `${plural(d.unassigned, 'stay')} had no room assigned yet and are not counted here.` : 'Stays that started in the period, by room.'}
                table={{ head: ['Room', 'Type', 'Stays', 'Nights', 'Revenue'], rows: d.byRoom.map((r) => [r.number, r.roomType, r.stays, r.nights, formatGhs(r.revenue)]) }}
              >
                <HBars rows={rooms.slice(0, 12).map((r) => ({ label: `Room ${r.number}`, value: r.stays, detail: `${r.roomType} · ${plural(r.nights, 'night')} · ${formatGhs(r.revenue)}` }))} valueName="Stays" emptyText="No rooms have been assigned to stays in this period." />
                {unbooked.length ? <p className="viz-note">Not booked at all: {unbooked.map((r) => r.number).join(', ')}.</p> : null}
              </ChartCard>

              <ChartCard title="Room types" note="Which kind of room guests choose." table={{ head: ['Room type', 'Stays', 'Nights', 'Revenue'], rows: d.byRoomType.map((r) => [r.name, r.stays, r.nights, formatGhs(r.revenue)]) }}>
                <HBars rows={d.byRoomType.map((r) => ({ label: r.name, value: r.stays, detail: `${plural(r.nights, 'night')} · ${formatGhs(r.revenue)}` }))} valueName="Stays" />
              </ChartCard>

              <ChartCard wide title="Occupancy night by night" note="Share of rooms with a guest staying." table={{ head: ['Night', 'Rooms occupied', 'Occupancy'], rows: d.occupancy.map((o) => [shortDate(o.date), o.occupied, fmtPct(o.pct)]) }}>
                <LineChart data={d.occupancy.map((o) => ({ label: shortDate(o.date), value: o.pct }))} format={fmtPct} valueName="Occupancy" max={100} />
              </ChartCard>

              <ChartCard title="What time guests book" note="Hour of the day a booking was made." table={{ head: ['Hour', 'Bookings'], rows: d.madeByHour.map((n, h) => [`${hh(h)}:00`, n]) }}>
                <Columns data={hourColumns(d.madeByHour)} valueName="Bookings" labelEvery={3} />
              </ChartCard>

              <ChartCard title="Which day bookings are made" note="Day of the week the booking was made." table={{ head: ['Day', 'Bookings made', 'Check-ins'], rows: WEEKDAYS.map((w, i) => [w, d.madeByWeekday[i]!, d.checkInsByWeekday[i]!]) }}>
                <Columns data={weekdayColumns(d.madeByWeekday)} valueName="Bookings" />
              </ChartCard>

              <ChartCard title="Arrival days" note="Day of the week stays start." table={{ head: ['Day', 'Check-ins'], rows: WEEKDAYS.map((w, i) => [w, d.checkInsByWeekday[i]!]) }}>
                <Columns data={weekdayColumns(d.checkInsByWeekday)} valueName="Check-ins" />
              </ChartCard>

              {d.byMonth.length > 1 ? (
                <ChartCard title="Busy and quiet months" note="Bookings made, and stays that started, each month." table={{ head: ['Month', 'Bookings made', 'Stays started'], rows: d.byMonth.map((m) => [monthLabel(m.month), m.made, m.stays]) }}>
                  <Columns data={d.byMonth.map((m) => ({ label: monthLabel(m.month), value: m.made, detail: `${plural(m.stays, 'stay')} started` }))} valueName="Bookings made" />
                </ChartCard>
              ) : null}

              <ChartCard title="How far ahead guests book" table={{ head: ['Booked', 'Bookings'], rows: d.leadTime.map((l) => [l.label, l.count]) }}>
                <HBars rows={d.leadTime.map((l) => ({ label: l.label, value: l.count }))} valueName="Bookings" />
              </ChartCard>

              <ChartCard title="Length of stay" table={{ head: ['Nights', 'Stays'], rows: d.stayLength.map((l) => [l.label, l.count]) }}>
                <HBars rows={d.stayLength.map((l) => ({ label: l.label, value: l.count }))} valueName="Stays" />
              </ChartCard>

              <ChartCard title="Where bookings come from" table={{ head: ['Source', 'Bookings'], rows: d.sources.map((s) => [labelled(SOURCE, s.source), s.count]) }}>
                <Split parts={d.sources.map((s) => ({ label: labelled(SOURCE, s.source), value: s.count }))} valueName="Bookings" />
              </ChartCard>
            </div>
          </>
        );
      }}
    </Frame>
  );
}

/* ------------------------------------------------------------------------------ restaurant */
function RestaurantTab({ from, to }: { from: string; to: string }) {
  const q = useInsights<RestaurantInsights>('restaurant', from, to);
  return (
    <Frame q={q}>
      {(d) => {
        const peak = argmax(d.byHour.map((h) => h.revenue));
        const peakDay = argmax(d.byWeekday);
        const money = fmtCedisShort;
        return (
          <>
            <div className="viz-tiles">
              <StatTile label="Paid sales" value={formatGhs(d.totals.paidSales)} hint={`${plural(d.totals.paidBills, 'bill')} paid`} />
              <StatTile label="Average bill" value={d.totals.averageBill ? formatGhs(d.totals.averageBill) : '—'} />
              <StatTile label="Items sold" value={fmtInt(d.totals.itemsSold)} hint={`${plural(d.totals.orders, 'order')}`} />
              <StatTile label="Ordered value" value={formatGhs(d.totals.ordered)} hint="including bills not yet paid" />
              <StatTile label="Discounts given" value={formatGhs(d.totals.discounts)} />
            </div>

            {peak >= 0 && d.totals.ordered > 0 ? (
              <p className="viz-callout">
                The restaurant and bar are busiest around <strong>{hh(peak)}:00</strong>
                {peakDay >= 0 ? (
                  <>
                    , and the best day is <strong>{WEEKDAYS[peakDay]}</strong>
                  </>
                ) : null}
                .
              </p>
            ) : null}

            <div className="viz-cards">
              <ChartCard title="Best sellers by quantity" note="Food and drink, most ordered first." table={{ head: ['Item', 'Type', 'Sold', 'Value'], rows: d.topByQty.map((i) => [i.name, labelled(DEPT, i.department), i.qty, formatGhs(i.revenue)]) }}>
                <HBars rows={d.topByQty.map((i) => ({ label: i.name, value: i.qty, detail: `${labelled(DEPT, i.department)} · ${formatGhs(i.revenue)}` }))} valueName="Sold" />
              </ChartCard>

              <ChartCard title="Best sellers by value" note="What brings in the most money." table={{ head: ['Item', 'Type', 'Sold', 'Value'], rows: d.topByRevenue.map((i) => [i.name, labelled(DEPT, i.department), i.qty, formatGhs(i.revenue)]) }}>
                <HBars rows={d.topByRevenue.map((i) => ({ label: i.name, value: i.revenue, detail: `${labelled(DEPT, i.department)} · ${plural(i.qty, 'sold', 'sold')}` }))} format={money} valueName="Value" />
              </ChartCard>

              <ChartCard title="Food and drink" note="Share of what was ordered." table={{ head: ['Type', 'Sold', 'Value'], rows: d.byDepartment.map((x) => [labelled(DEPT, x.department), x.qty, formatGhs(x.revenue)]) }}>
                <Split parts={d.byDepartment.map((x) => ({ label: labelled(DEPT, x.department), value: x.revenue }))} format={formatGhs} valueName="Value" />
              </ChartCard>

              <ChartCard title="How guests pay" note="Money taken on restaurant and bar bills." table={{ head: ['Paid by', 'Payments', 'Amount'], rows: d.tenders.map((t) => [labelled(TENDER, t.tender), t.count, formatGhs(t.amount)]) }}>
                <Split parts={d.tenders.map((t) => ({ label: labelled(TENDER, t.tender), value: t.amount }))} format={formatGhs} valueName="Amount" />
              </ChartCard>

              <ChartCard title="Sales by hour" note="Value ordered, by the hour of the day." table={{ head: ['Hour', 'Orders started', 'Items', 'Value'], rows: d.byHour.map((h) => [`${hh(h.hour)}:00`, h.orders, h.qty, formatGhs(h.revenue)]) }}>
                <Columns data={d.byHour.map((h) => ({ label: hh(h.hour), value: h.revenue, title: `${hh(h.hour)}:00 to ${hh(h.hour)}:59`, detail: `${plural(h.orders, 'order')} · ${plural(h.qty, 'item')}` }))} format={money} valueName="Value" labelEvery={3} />
              </ChartCard>

              <ChartCard title="Sales by day of the week" table={{ head: ['Day', 'Value'], rows: WEEKDAYS.map((w, i) => [w, formatGhs(d.byWeekday[i]!)]) }}>
                <Columns data={weekdayColumns(d.byWeekday)} format={money} valueName="Value" />
              </ChartCard>

              <ChartCard title="Menu categories" table={{ head: ['Category', 'Sold', 'Value'], rows: d.byCategory.map((c) => [c.category, c.qty, formatGhs(c.revenue)]) }}>
                <HBars rows={d.byCategory.map((c) => ({ label: c.category, value: c.revenue, detail: plural(c.qty, 'item') }))} format={money} valueName="Value" />
              </ChartCard>

              <ChartCard title="Sales by waiter" note="Value of the orders each person took." table={{ head: ['Waiter', 'Orders', 'Value'], rows: d.waiters.map((w) => [w.name, w.orders, formatGhs(w.revenue)]) }}>
                <HBars rows={d.waiters.map((w) => ({ label: w.name, value: w.revenue, detail: plural(w.orders, 'order') }))} format={money} valueName="Value" />
              </ChartCard>

              <ChartCard title="Busiest tables" table={{ head: ['Table', 'Orders'], rows: d.tables.map((t) => [t.name, t.orders]) }}>
                <HBars rows={d.tables.map((t) => ({ label: t.name, value: t.orders }))} valueName="Orders" emptyText="No table orders in this period." />
              </ChartCard>

              <ChartCard title="Not ordered at all" note="Active menu items nobody ordered in this period: worth a look.">
                {d.notSold.length ? (
                  <ul className="viz-chips">
                    {d.notSold.map((m) => (
                      <li key={m.name} title={m.category}>
                        {m.name}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <Empty>Every active item sold at least once.</Empty>
                )}
              </ChartCard>
            </div>
          </>
        );
      }}
    </Frame>
  );
}

/* ------------------------------------------------------------------------------ inventory */
const STATE = { OUT: { label: 'Out', tone: 'bad' as const }, LOW: { label: 'Low', tone: 'warn' as const }, OK: { label: 'OK', tone: 'good' as const }, NEW: { label: 'Not counted', tone: '' as const } };

/** One chart per counting word: grams with grams, sets with sets, never grams with glasses. */
function PerUnit({ title, note, data }: { title: string; note: string; data: { unit: string; rows: { name: string; value: number }[] }[] }) {
  return (
    <ChartCard title={title} note={note} table={{ head: ['Item', 'Quantity'], rows: data.flatMap((g) => g.rows.map((r) => [r.name, `${fmtInt(r.value)} ${g.unit}`])) }}>
      {data.length === 0 ? (
        <Empty />
      ) : (
        data.map((g) => (
          <div key={g.unit}>
            {data.length > 1 ? <p className="viz-note">Counted in {g.unit}</p> : null}
            <HBars rows={g.rows.map((r) => ({ label: r.name, value: r.value }))} format={(n) => `${fmtInt(n)} ${g.unit}`} valueName="Quantity" />
          </div>
        ))
      )}
    </ChartCard>
  );
}

function InventoryTab({ from, to }: { from: string; to: string }) {
  const [category, setCategory] = useState<StockCategory | ''>('');
  const q = useInsights<InventoryInsights>('inventory', from, to, category ? `&category=${category}` : '');
  return (
    <>
      <div className="viz-filters">
        <Field id="in-cat" label="Kind of item">
          <select id="in-cat" className="select" value={category} onChange={(e) => setCategory(e.target.value as StockCategory | '')}>
            <option value="">Everything</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {CATEGORY_LABEL[c]}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Frame q={q}>
        {(d) => {
          const urgent = d.stock.filter((s) => s.state === 'OUT' || s.state === 'LOW');
          return (
            <>
              <div className="viz-tiles">
                <StatTile label="Items tracked" value={fmtInt(d.totals.ingredients)} />
                <StatTile label="Out of stock" value={fmtInt(d.totals.out)} hint={d.totals.out ? 'buy these first' : 'nothing is out'} />
                <StatTile label="Running low" value={fmtInt(d.totals.low)} hint="at or below the reorder level" />
                <StatTile label="Not counted yet" value={fmtInt(d.totals.notCounted)} hint={d.totals.notCounted ? 'count them on the Inventory screen' : 'everything is counted'} />
                <StatTile label="Deliveries recorded" value={fmtInt(d.totals.receipts)} hint="opening counts not included" />
                <StatTile label="Broken, lost or wasted" value={fmtInt(d.totals.wasteEvents)} hint="records in this period" />
              </div>

              {urgent.length ? (
                <p className="viz-callout">
                  To buy first: <strong>{urgent.slice(0, 5).map((s) => s.name).join(', ')}</strong>
                  {urgent.length > 5 ? ` and ${urgent.length - 5} more` : ''}.
                </p>
              ) : null}

              <div className="viz-cards">
                {!category && d.byCategory.length > 0 ? (
                  <ChartCard title="By kind of item" note="How many items of each kind the hotel keeps count of." table={{ head: ['Kind', 'Items', 'Out', 'Low', 'Not counted'], rows: d.byCategory.map((c) => [CATEGORY_LABEL[c.category], c.items, c.out, c.low, c.notCounted]) }}>
                    <HBars rows={d.byCategory.map((c) => ({ label: CATEGORY_LABEL[c.category], value: c.items, detail: `${c.out} out · ${c.low} low · ${c.notCounted} not counted` }))} valueName="Items" />
                  </ChartCard>
                ) : null}

                <ChartCard
                  wide
                  title="Stock now"
                  note="The bar shows how much is on the shelf against twice the reorder level. Most urgent first."
                  table={{ head: ['Item', 'Kind', 'Kept in', 'On hand', 'Reorder at', 'Used per day', 'Days left', 'State'], rows: d.stock.map((s) => [s.name, CATEGORY_LABEL[s.category], s.location ?? '—', s.state === 'NEW' ? '—' : `${fmtInt(s.onHand)} ${s.unitWord}`, s.reorderLevel ? `${fmtInt(s.reorderLevel)} ${s.unitWord}` : '—', s.usedPerDay ? `${s.usedPerDay} ${s.unitWord}` : '—', s.daysLeft === null ? '—' : s.daysLeft, STATE[s.state].label]) }}
                >
                  {d.stock.length === 0 ? (
                    <Empty>No items are set up yet (Inventory screen).</Empty>
                  ) : (
                    d.stock.slice(0, 14).map((s) => {
                      const target = s.reorderLevel > 0 ? s.reorderLevel * 2 : Math.max(s.onHand, 1);
                      const pct = Math.max(0, Math.min(1, s.onHand / target));
                      const st = STATE[s.state];
                      return (
                        <div key={s.id} className="viz-meter">
                          <span title={`${s.name}${s.location ? ` (${s.location})` : ''}`}>{s.name}</span>
                          <span className="viz-meter-track" role="img" aria-label={s.state === 'NEW' ? `${s.name}: not counted yet` : `${s.name}: ${fmtInt(s.onHand)} ${s.unitWord} on hand`}>
                            {s.state !== 'NEW' ? <span className="viz-meter-fill" style={{ display: 'block', width: `${Math.max(pct * 100, s.onHand > 0 ? 3 : 0)}%`, background: `var(--${st.tone || 'good'})` }} /> : null}
                          </span>
                          <span className="row small" style={{ justifyContent: 'flex-end', gap: 8 }}>
                            <span className="num">{s.state === 'NEW' ? '—' : `${fmtInt(s.onHand)} ${s.unitWord}`}</span>
                            {s.daysLeft !== null ? <span className="muted">{s.daysLeft < 1 ? 'under a day' : `${Math.round(s.daysLeft)} d left`}</span> : null}
                            <Badge tone={st.tone}>{st.label}</Badge>
                          </span>
                        </div>
                      );
                    })
                  )}
                </ChartCard>

                <ChartCard
                  title="Regularly bought"
                  note="How many times each item was delivered in this period. The readout shows how much and how often."
                  table={{ head: ['Item', 'Deliveries', 'Total received', 'Every (days)', 'Last delivery'], rows: d.regularlyBought.map((b) => [b.name, b.times, `${fmtInt(b.received)} ${b.unitWord}`, b.averageGapDays ?? '—', b.lastReceived ? shortDate(b.lastReceived.slice(0, 10)) : '—']) }}
                >
                  <HBars
                    rows={d.regularlyBought.map((b) => ({ label: b.name, value: b.times, detail: `${fmtInt(b.received)} ${b.unitWord} in total${b.averageGapDays ? ` · about every ${b.averageGapDays} days` : ''}${b.lastReceived ? ` · last ${shortDate(b.lastReceived.slice(0, 10))}` : ''}` }))}
                    valueName="Deliveries"
                    emptyText="No deliveries were recorded in this period."
                  />
                </ChartCard>

                <PerUnit title="Most used" note="Used by orders, or recorded as used or issued, in this period." data={d.mostUsed} />
                <PerUnit title="Most broken, lost or wasted" note="Recorded as waste, breakage or loss, in this period." data={d.mostWasted} />

                <ChartCard title="Deliveries each week" table={{ head: ['Week starting', 'Deliveries', 'Waste records'], rows: d.weekly.map((w) => [shortDate(w.week), w.receipts, w.waste]) }}>
                  <Columns data={d.weekly.map((w) => ({ label: shortDate(w.week), value: w.receipts, title: `Week of ${shortDate(w.week)}`, detail: `${plural(w.waste, 'waste record')}` }))} valueName="Deliveries" emphasis={false} labelEvery={Math.max(1, Math.ceil(d.weekly.length / 6))} emptyText="No deliveries were recorded in this period." />
                </ChartCard>
              </div>
              <p className="viz-note">Purchase prices are not recorded in the system, so spending cannot be charted. Quantities counted in different words (grams, sets, pieces) are always shown separately.</p>
            </>
          );
        }}
      </Frame>
    </>
  );
}

/* ------------------------------------------------------------------------------ activity */
function ActivityTab({ from, to }: { from: string; to: string }) {
  const q = useInsights<ActivityInsights>('activity', from, to);
  return (
    <Frame q={q}>
      {(d) => {
        const hour = argmax(d.byHour);
        const people = d.byPerson.filter((p) => p.id);
        return (
          <>
            <div className="viz-tiles">
              <StatTile label="Recorded events" value={fmtInt(d.total)} hint="in the audit log" />
              <StatTile label="People active" value={fmtInt(people.length)} />
              <StatTile label="Busiest hour" value={hour >= 0 ? `${hh(hour)}:00` : '—'} />
              <StatTile label="Most active" value={people[0]?.name ?? '—'} hint={people[0] ? `${plural(people[0].events, 'event')}` : undefined} />
            </div>
            <div className="viz-cards">
              <ChartCard wide title="Activity each day" note="Everything the system recorded: sign-ins, bookings, payments, stock and more." table={{ head: ['Day', 'Events'], rows: d.byDay.map((x) => [shortDate(x.date), x.count]) }}>
                {d.byDay.length > 45 ? (
                  <LineChart data={d.byDay.map((x) => ({ label: shortDate(x.date), value: x.count }))} valueName="Events" />
                ) : (
                  <Columns data={d.byDay.map((x) => ({ label: shortDate(x.date), value: x.count }))} valueName="Events" emphasis={false} labelEvery={Math.max(1, Math.ceil(d.byDay.length / 8))} />
                )}
              </ChartCard>

              <ChartCard title="Who did what" note="Events by person." table={{ head: ['Person', 'Role', 'Events'], rows: d.byPerson.map((p) => [p.name, p.role ? labelled(ROLE, p.role) : '—', p.events]) }}>
                <HBars rows={d.byPerson.map((p) => ({ label: p.name, value: p.events, detail: p.role ? labelled(ROLE, p.role) : 'Automatic' }))} valueName="Events" />
              </ChartCard>

              <ChartCard title="What was done" note="Events by kind of work." table={{ head: ['Kind', 'Events'], rows: d.byGroup.map((g) => [labelled(GROUP, g.group), g.count]) }}>
                <HBars rows={d.byGroup.slice(0, 10).map((g) => ({ label: labelled(GROUP, g.group), value: g.count }))} valueName="Events" />
              </ChartCard>

              <ChartCard title="Time of day" note="When the system is used." table={{ head: ['Hour', 'Events'], rows: d.byHour.map((n, h) => [`${hh(h)}:00`, n]) }}>
                <Columns data={hourColumns(d.byHour)} valueName="Events" labelEvery={3} />
              </ChartCard>

              <ChartCard title="Most common actions" table={{ head: ['Action', 'Events'], rows: d.topActions.map((a) => [prettyAction(a.action), a.count]) }}>
                <HBars rows={d.topActions.map((a) => ({ label: prettyAction(a.action), value: a.count }))} valueName="Events" />
              </ChartCard>
            </div>
            <p className="viz-note">
              Each event, with the person and the details, is in the <Link to="/audit">audit log</Link>.
            </p>
          </>
        );
      }}
    </Frame>
  );
}
